// src/components/leads/b2c/UploadCustomersModal.js
// Sliding Panel for Bulk Customer Upload via CSV/Excel
// Matches the design pattern of AddCustomerModal

import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';

export default function UploadCustomersModal({ onClose, onUpload }) {
  const [activeStep, setActiveStep] = useState('instructions'); // 'instructions', 'upload', 'preview', 'processing'
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [validData, setValidData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Template data for download
  const templateData = [
    {
      'Customer Name': 'Sarah Johnson',
      'Phone Number': '07700123456',
      'Email Address': 'sarah@example.com',
      'Source': 'instagram',
      'Notes': 'Interested in balayage treatment'
    },
    {
      'Customer Name': 'Mike Smith',
      'Phone Number': '+44 7700987654',
      'Email Address': 'mike@example.com',
      'Source': 'facebook',
      'Notes': 'Regular customer, prefers weekends'
    },
    {
      'Customer Name': 'Emma Williams',
      'Phone Number': '07711222333',
      'Email Address': '',
      'Source': 'referral',
      'Notes': 'Referred by Sarah Johnson'
    }
  ];

  // Download CSV template
  const handleDownloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Customers Template');
    
    // Set column widths
    ws['!cols'] = [
      { wch: 20 }, // Customer Name
      { wch: 18 }, // Phone Number
      { wch: 25 }, // Email Address
      { wch: 15 }, // Source
      { wch: 40 }  // Notes
    ];
    
    XLSX.writeFile(wb, 'customer_import_template.xlsx');
    console.log('✅ Template downloaded');
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    console.log('📁 File selected:', selectedFile.name);
    setFile(selectedFile);
    setErrors([]);
    
    // Parse the file
    parseFile(selectedFile);
  };

  // Parse CSV/Excel file
  const parseFile = (file) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        console.log('📊 Parsed data:', jsonData);
        
        if (jsonData.length === 0) {
          setErrors(['The file appears to be empty. Please add customer data.']);
          return;
        }
        
        setParsedData(jsonData);
        validateData(jsonData, file.name);
        setActiveStep('preview');
        
      } catch (error) {
        console.error('❌ Parse error:', error);
        setErrors(['Failed to parse file. Please ensure it\'s a valid Excel or CSV file.']);
      }
    };
    
    reader.readAsBinaryString(file);
  };

  // Validate parsed data
  const validateData = (data, filename) => {
    const validated = [];
    const validationErrors = [];
    
    data.forEach((row, index) => {
      const rowNumber = index + 2; // +2 because Excel starts at 1 and has header
      const errors = [];
      
      // Check required field: Customer Name
      const name = row['Customer Name'] || row['Name'] || row['name'] || '';
      if (!name.trim()) {
        errors.push(`Row ${rowNumber}: Customer Name is required`);
      }
      
      // Get other fields (all optional)
      const phone = row['Phone Number'] || row['Phone'] || row['phone'] || '';
      const email = row['Email Address'] || row['Email'] || row['email'] || '';
      const source = row['Source'] || row['source'] || 'csv_upload';
      const notes = row['Notes'] || row['notes'] || '';
      
      // Validate email format if provided
      if (email && email.trim() !== '') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          errors.push(`Row ${rowNumber}: Invalid email format - ${email}`);
        }
      }
      
      // Validate source
      const validSources = ['instagram', 'facebook', 'whatsapp', 'linkedin', 'phone', 'email', 'website', 'referral', 'walkin', 'csv_upload', 'other'];
      const normalizedSource = source.toLowerCase().trim();
      if (!validSources.includes(normalizedSource)) {
        // Don't error, just default to csv_upload
        console.log(`⚠️ Row ${rowNumber}: Invalid source "${source}", using "csv_upload"`);
      }
      
      if (errors.length === 0) {
        validated.push({
          name: name.trim(),
          phone: phone.trim() || null,
          email: email.trim() || null,
          source: validSources.includes(normalizedSource) ? normalizedSource : 'csv_upload',
          notes: notes.trim() || '',
          status: 'new',
          sourceDetails: `Imported from CSV: ${filename}`
        });
      } else {
        validationErrors.push(...errors);
      }
    });
    
    console.log('✅ Validated:', validated.length, 'rows');
    console.log('❌ Errors:', validationErrors.length, 'issues');
    
    setValidData(validated);
    setErrors(validationErrors);
  };

  // Handle upload
  const handleUpload = async () => {
    if (validData.length === 0) {
      setErrors(['No valid data to upload. Please fix the errors and try again.']);
      return;
    }

    setUploading(true);
    setActiveStep('processing');

    try {
      console.log('📤 Uploading', validData.length, 'customers...');
      await onUpload(validData);
      
      console.log('✅ Upload complete');
      
      // Show success briefly before closing
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (error) {
      console.error('❌ Upload error:', error);
      setErrors(['Failed to upload customers. Please try again.']);
      setActiveStep('preview');
    } finally {
      setUploading(false);
    }
  };

  // Render instructions panel
  const renderInstructions = () => (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3 pb-4 border-b border-gray-200">
        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
          <i className="fas fa-file-upload text-blue-600 text-xl"></i>
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">Bulk Customer Import</h3>
          <p className="text-sm text-gray-600">Upload Excel or CSV file with customer data</p>
        </div>
      </div>

      {/* Step 1: Download Template */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
            1
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-bold text-gray-900 mb-2">Download Template</h4>
            <p className="text-sm text-gray-700 mb-3">
              Start with our template to ensure your data is formatted correctly.
            </p>
            <button
              onClick={handleDownloadTemplate}
              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
            >
              <i className="fas fa-download mr-2"></i>
              Download Template
            </button>
          </div>
        </div>
      </div>

      {/* Step 2: Fill in Data */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
            2
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-bold text-gray-900 mb-2">Fill in Customer Data</h4>
            <p className="text-sm text-gray-700 mb-3">
              Open the template and add your customer information:
            </p>
            <ul className="text-sm text-gray-700 space-y-1.5 ml-4">
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span><strong>Customer Name</strong> - Required</span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-400 mr-2">○</span>
                <span><strong>Phone Number</strong> - Optional (e.g., 07700123456 or +44 7700123456)</span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-400 mr-2">○</span>
                <span><strong>Email Address</strong> - Optional (must be valid email format)</span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-400 mr-2">○</span>
                <span><strong>Source</strong> - Optional (instagram, facebook, whatsapp, referral, etc.)</span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-400 mr-2">○</span>
                <span><strong>Notes</strong> - Optional (any additional information)</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Step 3: Upload */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
            3
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-bold text-gray-900 mb-2">Upload File</h4>
            <p className="text-sm text-gray-700 mb-3">
              Save your file and upload it here. We'll validate the data before importing.
            </p>
            <button
              onClick={() => setActiveStep('upload')}
              className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
            >
              <i className="fas fa-arrow-right mr-2"></i>
              Continue to Upload
            </button>
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center">
          <i className="fas fa-lightbulb text-yellow-500 mr-2"></i>
          Tips for Best Results
        </h4>
        <ul className="text-sm text-gray-700 space-y-1 ml-6">
          <li>• Keep the column headers exactly as shown in the template</li>
          <li>• Ensure Customer Name is filled for every row</li>
          <li>• Phone numbers can include country codes (e.g., +44)</li>
          <li>• Email addresses must be valid format (e.g., name@example.com)</li>
          <li>• Source will default to "csv_upload" if not specified</li>
          <li>• Excel (.xlsx) or CSV (.csv) formats are supported</li>
        </ul>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <button
          onClick={onClose}
          className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  // Render upload panel
  const renderUpload = () => (
    <div className="p-6 space-y-4">
      <button
        onClick={() => setActiveStep('instructions')}
        className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
      >
        <i className="fas fa-arrow-left mr-2"></i>
        Back to Instructions
      </button>

      {/* Upload Area */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-600 transition-colors">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="fas fa-cloud-upload-alt text-blue-600 text-2xl"></i>
        </div>
        
        <h3 className="text-lg font-bold text-gray-900 mb-2">Upload Customer File</h3>
        <p className="text-sm text-gray-600 mb-4">
          Drop your Excel or CSV file here, or click to browse
        </p>
        
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileSelect}
          className="hidden"
        />
        
        <button
          onClick={() => fileInputRef.current?.click()}
          className="px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          <i className="fas fa-folder-open mr-2"></i>
          Choose File
        </button>
        
        <p className="text-xs text-gray-500 mt-3">
          Supported formats: .xlsx, .xls, .csv (Max 10MB)
        </p>
      </div>

      {/* Selected File */}
      {file && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <i className="fas fa-file-excel text-green-600 text-2xl"></i>
            <div>
              <p className="text-sm font-medium text-gray-900">{file.name}</p>
              <p className="text-xs text-gray-600">{(file.size / 1024).toFixed(2)} KB</p>
            </div>
          </div>
          <button
            onClick={() => {
              setFile(null);
              setParsedData([]);
              setValidData([]);
              setErrors([]);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
            className="text-red-600 hover:text-red-700"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm font-medium text-red-800 mb-2">
            <i className="fas fa-exclamation-circle mr-2"></i>
            Error reading file
          </p>
          <ul className="text-sm text-red-700 space-y-1">
            {errors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  // Render preview panel
  const renderPreview = () => (
    <div className="p-6 space-y-4">
      <button
        onClick={() => setActiveStep('upload')}
        className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
      >
        <i className="fas fa-arrow-left mr-2"></i>
        Back
      </button>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{parsedData.length}</p>
          <p className="text-xs text-gray-600">Total Rows</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{validData.length}</p>
          <p className="text-xs text-gray-600">Valid</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-red-600">{errors.length}</p>
          <p className="text-xs text-gray-600">Errors</p>
        </div>
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-48 overflow-y-auto">
          <p className="text-sm font-medium text-red-800 mb-2">
            <i className="fas fa-exclamation-triangle mr-2"></i>
            Validation Errors ({errors.length})
          </p>
          <ul className="text-sm text-red-700 space-y-1">
            {errors.map((error, index) => (
              <li key={index} className="text-xs">• {error}</li>
            ))}
          </ul>
          <p className="text-xs text-red-600 mt-3">
            Please fix these errors in your file and upload again.
          </p>
        </div>
      )}

      {/* Preview Table */}
      {validData.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h4 className="text-sm font-bold text-gray-900">
              Preview - First {Math.min(validData.length, 5)} Customers
            </h4>
          </div>
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Name</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Phone</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Email</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-700">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {validData.slice(0, 5).map((customer, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-900">{customer.name}</td>
                    <td className="px-4 py-2 text-gray-600">{customer.phone || '-'}</td>
                    <td className="px-4 py-2 text-gray-600">{customer.email || '-'}</td>
                    <td className="px-4 py-2">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                        {customer.source}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {validData.length > 5 && (
            <div className="bg-gray-50 px-4 py-2 border-t border-gray-200 text-center">
              <p className="text-xs text-gray-600">
                + {validData.length - 5} more customers
              </p>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <button
          onClick={onClose}
          className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleUpload}
          disabled={validData.length === 0}
          className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          <i className="fas fa-upload mr-2"></i>
          Import {validData.length} Customers
        </button>
      </div>
    </div>
  );

  // Render processing panel
  const renderProcessing = () => (
    <div className="p-6 flex flex-col items-center justify-center min-h-[400px]">
      <div className="w-20 h-20 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-6"></div>
      <h3 className="text-lg font-bold text-gray-900 mb-2">Importing Customers...</h3>
      <p className="text-sm text-gray-600 text-center">
        Adding {validData.length} customers to your database
      </p>
    </div>
  );

  // Render content based on active step
  const renderContent = () => {
    switch (activeStep) {
      case 'upload':
        return renderUpload();
      case 'preview':
        return renderPreview();
      case 'processing':
        return renderProcessing();
      default:
        return renderInstructions();
    }
  };

  return createPortal(
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={uploading ? undefined : onClose}
      ></div>
      
      <div className="fixed right-0 top-0 h-full w-full max-w-3xl bg-white shadow-2xl z-50 overflow-y-auto animate-slide-in">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-300 p-6 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {activeStep === 'upload' ? 'Upload File' :
                 activeStep === 'preview' ? 'Preview & Confirm' :
                 activeStep === 'processing' ? 'Processing...' :
                 'Bulk Customer Import'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {activeStep === 'upload' ? 'Select your customer data file' :
                 activeStep === 'preview' ? 'Review customers before importing' :
                 activeStep === 'processing' ? 'Please wait...' :
                 'Import multiple customers at once'}
              </p>
            </div>
            {!uploading && (
              <button
                onClick={onClose}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        {renderContent()}
      </div>

      <style>{`
        @keyframes slide-in {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </>,
    document.body
  );
}